import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, milliseconds, validate } from "../../../zod-decorator/index.js";
import { ManualOverrideInfo } from "../../commonSubSchemas/manualOverrideInfo.js";
import { RecentSensorDataPoint } from "../../commonSubSchemas/recentSensorDataPoint.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type { Gen4NetworkInfoFieldsWithDefaults, Gen4NetworkInfoSchemaTypes } from "../../types/gen4NetworkInfo.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";
import { SignalMetrics } from "./signalMetrics.js";
import { SproutLastDownlinkInfo } from "./sproutLastDownlinkInfo.js";
import { SproutRecentPortHistory } from "./sproutRecentPortHistory.js";

const SCHEMA_KEY = "Gen4NetworkInfo" as const;
const RECENT_DOWNLINKS_MAX_LENGTH = 3;

// TODO: convert to responseOnly schema class
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<Gen4NetworkInfo>>(...addSchemaKeyHook(SCHEMA_KEY))
export class Gen4NetworkInfo extends SchemaBase<Gen4NetworkInfo, Gen4NetworkInfoFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * Information on the most recent few downlinks that we attempted to sent. This is useful for
     * detecting when these downlinks were received and updating the gen4InternalStateTracker appropriately.
     */
    @validate({
        childClass: SproutLastDownlinkInfo,
        childClassTransform: (object) => z.record(z.string(), object),
        responseOnly: true,
    })
    @prop({ type: () => SproutLastDownlinkInfo, required: true }, PropType.MAP)
    public recentDownlinks!: Map<string, SproutLastDownlinkInfo>;

    /**
     * The date that the last network test was started on this device. This is used to determine if the device
     * is currently undergoing a network test. See Gen4Config.ts for details on how long a network test
     * lasts
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastNetworkTestStartDate?: Date;

    /**
     * Keeps track of what was most recently sent and received on each fport on the device.
     */
    @validate({
        childClass: SproutRecentPortHistory,
        childClassTransform: (object) => z.record(z.string(), object),
        responseOnly: true,
    })
    @prop({ type: () => SproutRecentPortHistory, required: true }, PropType.MAP)
    public lastContactByPort!: Map<string, SproutRecentPortHistory>;

    /**
     * Shows the last non-zero error code uplinked by the device.
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: -1 })
    public lastErrorCode: number = -1;

    /**
     * RSSI and SNR of the connection to each gateway as measured during the most recent uplink
     */
    @validate({
        childClass: SignalMetrics,
        childClassTransform: (object) => z.array(object).optional(),
        responseOnly: true,
    })
    @prop({ type: () => SignalMetrics, required: true, default: [] }, PropType.ARRAY)
    public lastSignalMetrics: SignalMetrics[] = [];

    /**
     * Overall packet loss, up and down. Between 0 and 1, lower is better
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public packetLoss: number = 0;

    /**
     * Percentage of uplinks that were sent by the device, but not received by the backend. Between 0 and 1, lower is better
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public uplinkPacketLoss: number = 0;

    /**
     * Percentage of downlinks that were sent by the backend, but not received by the device. Between 0 and 1, lower is better
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public downlinkPacketLoss: number = 0;

    /**
     * The number of crashes the device has reported
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public crashCount: number = 0;

    /**
     * The number of resets the device has reported
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public resetCount: number = 0;

    @validate({
        childClass: ManualOverrideInfo,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => ManualOverrideInfo, required: false })
    public manualOverrideInfo?: ManualOverrideInfo;

    /**
     * Device uplink period in milliseconds.
     *
     * Stored here as a multiple of 15 seconds, but encoded
     * during downlinks as ⌊currentStandardUplinkPeriod / 15000⌋.
     *
     * Gen4 devices support a value range of [1, 254] which represents
     * the number of 15-second intervals between uplinks, yielding an
     * effective range of [15 seconds, 63.5 minutes] between uplinks.
     */
    @validate({ zodValidator: milliseconds.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public currentStandardUplinkPeriod?: number;

    /**
     * The most recent gen4ValvePulseRecord that was measured by this device, includes the solenoid pulse
     * code.
     */
    @validate({
        childClass: RecentSensorDataPoint,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => RecentSensorDataPoint, required: false })
    public lastValvePulseRecord?: RecentSensorDataPoint<"gen4ValvePulseRecord">;

    /**
     * The most recent battery voltage reading that was measured by this device.
     */
    @validate({
        childClass: RecentSensorDataPoint,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => RecentSensorDataPoint, required: false })
    public lastBatteryVoltageReading?: RecentSensorDataPoint<"batteryVoltage">;

    static getters = {
        /**
         * Returns the most recent downlink from `recentDownlinks` or `null` if no downlinks are
         * available.
         *
         * @param networkInfo - The `Gen4NetworkInfo` object containing `recentDownlinks`
         * @returns The most recent downlink information as a `SproutLastDownlinkInfo` object,
         *          or `null` if no downlinks are present.
         */
        getMostRecentDownlink: (self: Gen4NetworkInfo): SproutLastDownlinkInfo | null => {
            const recentDownlinks = self?.recentDownlinks ?? new Map();
            const reverseChronologicalDownlinks = Array.from(recentDownlinks?.values() ?? []).sort(
                (a, b) => b.date.valueOf() - a.date.valueOf(),
            );
            return reverseChronologicalDownlinks.length > 0 ? reverseChronologicalDownlinks[0] : null;
        },
    } as const satisfies Record<string, (self: Gen4NetworkInfo) => any>;

    static utilities = {
        /**
         * Appends the provided `currentDownlink` to the `recentDownlinks` of the given `networkInfo`.
         * Ensures that the `recentDownlinks` object does not exceed the maximum allowed length
         * (`RECENT_DOWNLINKS_MAX_LENGTH`) by removing the oldest entries as needed.
         *
         * If `recentDownlinks` is not initialized, it will be created and populated with the
         * `currentDownlink`. If the `currentDownlink` already exists in `recentDownlinks` (based on
         * its `idemp` property), no action is taken.
         *
         * @param networkInfo - The `Gen4NetworkInfo` object containing the `recentDownlinks` to update.
         * @param currentDownlink - The `SproutLastDownlinkInfo` object to append to `recentDownlinks`.
         */
        appendToRecentDownlinks: (self: Gen4NetworkInfo, currentDownlink: SproutLastDownlinkInfo): void => {
            if (!self.recentDownlinks) {
                self.recentDownlinks = new Map();
            }

            const recentDownlinks = self.recentDownlinks;
            recentDownlinks.set(currentDownlink.idemp.toString(), currentDownlink);
            const chronologicalIdempotencyTokens = Array.from(recentDownlinks?.values() ?? [])
                .sort((a, b) => a.date.valueOf() - b.date.valueOf())
                .map((downlink) => downlink.idemp);
            while (chronologicalIdempotencyTokens.length > RECENT_DOWNLINKS_MAX_LENGTH) {
                const oldestIdempotencyToken = chronologicalIdempotencyTokens.shift();
                recentDownlinks.delete(oldestIdempotencyToken?.toString() ?? "");
            }
        },
        getDefaultGen4NetworkInfo: (): Gen4NetworkInfo =>
            ({
                crashCount: 0,
                downlinkPacketLoss: 0,
                lastContactByPort: new Map(),
                recentDownlinks: new Map(),
                manualOverrideInfo: {
                    remoteOverrideSetting: "none",
                    remoteOverrideSettingLastUpdated: new Date(0),
                    deviceOverrideSetting: "none",
                    deviceOverrideSettingLastUpdated: new Date(0),
                },
                lastErrorCode: 0,
                lastSignalMetrics: [],
                packetLoss: 0,
                resetCount: 0,
                uplinkPacketLoss: 0,
            }) as Gen4NetworkInfo,
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<Gen4NetworkInfoSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<Gen4NetworkInfo, Gen4NetworkInfoFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

Gen4NetworkInfo.dtoValidators = getZodSchemaFromClass(
    Gen4NetworkInfo,
) as unknown as DtoValidatorTypes<Gen4NetworkInfoSchemaTypes>;
